import { Button, Col, DatePicker, Form, Input, Row, Table, Tooltip, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { TimeRangePickerProps } from 'antd/lib';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { SelectAirline } from '../../../../components/common/FormItem/SelectCustomFeilds';
import { useLazyGetPriorityClientFlightTrackerQuery } from '../../AirlineWiseSalesReport/endpoints/AirlineWiseSalesReportEndpoints';
import { IAirTicketSummary } from '../Interfaces/AirTicketReport.interfaces';

const PriorityClientFlightTracker = () => {
  const [form] = Form.useForm();
  const [fetchDATA, { data, isLoading }] = useLazyGetPriorityClientFlightTrackerQuery();
  const [page, setPage] = useState(1);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(100);
  const [searchParams, setSearchParams] = useSearchParams();
  const onFinish = (values: any) => {
    const { airline_id, date, segment, search } = values;
    const filterData: any = {
      page: currentPage,
      limit: pageSize,
    };

    const params: any = {};

    if (airline_id) {
      filterData.airline_id = airline_id;
      params.airline_id = airline_id;
    }

    if (search) {
      filterData.search = search;
      params.search = search;
    }
    if (date?.length) {
      filterData.from_date = dayjs(date[0]).format('YYYY-MM-DD');
      filterData.to_date = dayjs(date[1]).format('YYYY-MM-DD');
      params.from_date = filterData.from_date;
      params.to_date = filterData.to_date;
    }

    params.page = currentPage;
    params.limit = pageSize;

    setSearchParams(params); // updates URL
    fetchDATA(filterData);
  };
  useEffect(() => {
    const airline_id = searchParams.get('airline_id');
    const search = searchParams.get('search');
    const from_date = searchParams.get('from_date');
    const to_date = searchParams.get('to_date');
    const pageParam = Number(searchParams.get('page') || 1);
    const limitParam = Number(searchParams.get('limit') || 100);

    const initValues: any = {};

    if (airline_id) initValues.airline_id = airline_id;
    if (search) initValues.search = search;
    if (from_date && to_date) {
      initValues.date = [dayjs(from_date), dayjs(to_date)];
    } else {
      initValues.date = [dayjs(), dayjs()];
    }

    form.setFieldsValue(initValues);

    setPage(pageParam);
    setPageSize(limitParam);
    setCurrentPage(pageParam);

    // Automatically trigger form submission with prefilled values
    form.submit();
  }, []);
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes blinker {
        50% { opacity: 0; }
      }
    `;
    document.head.appendChild(style);
  }, []);
  const priorityColumn = (): ColumnsType<IAirTicketSummary> => [
    {
      title: 'SL',
      render: (_text, _record, index) => (page - 1) * pageSize + index + 1,
      width: 60,
      align: 'center',
    },
    {
      title: (
        <>
          <p className='table-column-title'>Client Name</p>
          <p className='table-column-title'>Mobile No</p>
        </>
      ),
      render: (record) => (
        <>
          <Link
            target='_blank'
            to={
              record.airticket_client_id
                ? `/clients/details/client-${record.airticket_client_id}`
                : `/combineClients/details/${record.airticket_combined_id}`
            }
            state={record.airticket_client_id ? `/clients` : `/combineds`}
          >
            {record.client_name}
          </Link>
          <p>{record?.client_mobile}</p>
        </>
      ),
    },
    {
      title: (
        <>
          <p className='table-column-title'>Ticket No</p>
          <p className='table-column-title'>Airline</p>
        </>
      ),
      render: (record) => (
        <p>
          <Tooltip style={{ cursor: 'pointer' }} placement='topLeft' title={record?.airline_name}>
            {record?.airline_code}
          </Tooltip>
          {record?.airticket_ticket_no && ' - '}
          {record?.airticket_ticket_no}
        </p>
      ),
    },

    {
      title: (
        <>
          <p className='table-column-title'>Journey Date</p>
        </>
      ),
      render: (record) => (
        <>
          <p
            style={{
              animation: record.airticket_journey_date ? 'blinker 2s linear infinite' : 'none',
              color: record.airticket_journey_date ? 'red' : 'inherit',
              fontWeight: record.airticket_journey_date ? 'bold' : 'inherit',
            }}
          >
            {record.airticket_journey_date
              ? dayjs(record.airticket_journey_date).format('DD MMM YYYY')
              : ''}
          </p>
        </>
      ),
    },
    {
      title: (
        <>
          <p className='table-column-title'>Return Date</p>
        </>
      ),
      render: (record) => (
        <>
          <p
            style={{
              animation: record.airticket_return_date ? 'blinker 2s linear infinite' : 'none',
              color: record.airticket_return_date ? 'red' : 'inherit',
              fontWeight: record.airticket_journey_date ? 'bold' : 'inherit',
            }}
          >
            {record.airticket_return_date
              ? dayjs(record.airticket_return_date).format('DD MMM YYYY')
              : ''}
          </p>
        </>
      ),
    },
    {
      title: 'PNR',
      dataIndex: 'airticket_pnr',
      key: 'airticket_pnr',
    },
    {
      title: 'Routes',
      render: (record) => <p>{record?.airticket_routes}</p>,
    },
    {
      title: (
        <>
          <p className='table-column-title'>Sales Date</p>
          <p className='table-column-title'>Invoice No</p>
        </>
      ),
      render: (record) => (
        <>
          <p>{dayjs(record?.create_date).format('DD MMM YYYY')}</p>
          <Link target='_blank' to={`/airticket/details/${record.invoice_id}`}>
            {record?.invoice_no}
          </Link>
        </>
      ),
    },

    {
      title: 'Issued By',
      render: (record) => <p>{record?.issued_by}</p>,
    },
  ];
  const rangePresets: TimeRangePickerProps['presets'] = [
    { label: 'Today', value: [dayjs(), dayjs()] },
    { label: 'Yesterday', value: [dayjs().add(-1, 'd'), dayjs()] },
    { label: 'Last 7 Days', value: [dayjs().add(-7, 'd'), dayjs()] },
    { label: 'Last 14 Days', value: [dayjs().add(-14, 'd'), dayjs()] },
    { label: 'Last 30 Days', value: [dayjs().add(-30, 'd'), dayjs()] },
    { label: 'Last 90 Days', value: [dayjs().add(-90, 'd'), dayjs()] },
  ];
  return (
    <div>
      <Typography.Title level={3} style={{ margin: '5px 5px' }}>
        Priority Client Flight Tracker
      </Typography.Title>
      <Form
        onFinish={onFinish}
        layout='vertical'
        form={form}
        initialValues={{ date: [dayjs(), dayjs()], airline_id: 'all' }}
      >
        <Row gutter={[5, 2]} align='middle'>
          <SelectAirline
            required
            name='airline_id'
            label='Select Airline'
            size={4}
            showAll
            allowClear={false}
          />

          <Col span={6} xs={24} sm={24} md={4} lg={4}>
            <Form.Item label='Search' name='search'>
              <Input placeholder='Search by ticket no./PNR' allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={24} lg={4}>
            <Form.Item
              name='date'
              label='Select Date'
              rules={[{ required: true, message: 'Please select date' }]}
            >
              <DatePicker.RangePicker
                style={{ width: '100%' }}
                presets={rangePresets}
                format={'DD-MM-YYYY'}
                allowClear={false}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={24} lg={6} xl={3} xxl={2}>
            <Form.Item>
              <Button
                type='primary'
                htmlType='submit'
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  marginTop: '28px',
                }}
              >
                Search
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
        <Typography.Text>You have total: {data?.count} rows</Typography.Text>
      </div>

      <Table
        size='small'
        bordered
        rowKey='invoice_id'
        dataSource={data?.data || []}
        columns={priorityColumn()}
        scroll={{ x: 'max-content' }}
        loading={isLoading}
        pagination={{
          total: data?.count || 0,
          current: page,
          pageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions: ['100', '200', '500'],
          onChange: (newPage, newPageSize) => {
            setPage(newPage);
            setPageSize(newPageSize);
            setCurrentPage(newPage);
            form.submit(); // triggers onFinish and URL update
          },
        }}
      />
    </div>
  );
};

export default PriorityClientFlightTracker;
